import React, { useEffect, useState, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'

// Lightweight global email capture modal.
// It listens for the `openEmailModal` custom event and handles the same
// account creation + email collection flow used by the CTA section.
const EmailCaptureModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [source, setSource] = useState('')
  const inputRef = useRef(null)
  const posthog = usePostHog()

  // Open/close modal via custom events so any CTA can trigger it.
  useEffect(() => {
    const handleOpen = (event) => {
      if (event?.detail?.email) {
        setEmail(event.detail.email)
      }
      if (event?.detail?.source) {
        setSource(event.detail.source)
      }
      setIsOpen(true)
      setTimeout(() => inputRef.current?.focus(), 100)
    }

    const handleClose = () => {
      setIsOpen(false)
      setError('')
      setIsLoading(false)
      setShowSuccess(false)
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('openEmailModal', handleOpen)
      window.addEventListener('closeEmailModal', handleClose)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('openEmailModal', handleOpen)
        window.removeEventListener('closeEmailModal', handleClose)
      }
    }
  }, [])

  const generateStrongPassword = () => {
    const length = 32
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?'
    const values = crypto.getRandomValues(new Uint32Array(length))
    let password = ''
    for (let i = 0; i < length; i++) {
      password += charset[values[i] % charset.length]
    }
    return password
  }

  const createUserAccount = async (emailToUse) => {
    try {
      const password = generateStrongPassword()

      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: '',
          email: emailToUse,
          password: password,
          phone_number: 0,
          reason_of_automate: 'I came from CTA section for automation',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        if (
          response.status === 403 &&
          (errorData.code === 'ERROR_CODE_ACCESS_DENIED' || errorData.message?.includes('already in use'))
        ) {
          return { exists: true }
        }

        console.error('Failed to create user account:', errorData)
        throw new Error('Failed to create user account')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken, password }
    } catch (err) {
      console.error('Error creating user account:', err)
      return null
    }
  }

  const loginWithEmail = async (emailToUse) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/login_with_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailToUse,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to login with email:', errorData)
        throw new Error('Failed to login with email')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken }
    } catch (err) {
      console.error('Error logging in with email:', err)
      return null
    }
  }

  const sendUserCredentials = async (emailToUse, password) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/send_mail_user_credential', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailToUse,
          password_text: password,
        }),
      })

      if (!response.ok) {
        console.warn('Failed to send credentials email (non-critical):', await response.text().catch(() => ''))
      }
    } catch (err) {
      console.warn('Error sending credentials email (non-critical):', err)
    }
  }

  const validateEmail = (emailToUse) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailToUse)
  }

  const collectEmail = async (emailValue) => {
    // Create user account first
    let userResult = await createUserAccount(emailValue)

    if (!userResult) {
      return { success: false, message: 'Failed to create account. Please try again.' }
    }

    // Check if user already exists
    if ('exists' in userResult && userResult.exists) {
      // User exists, login instead
      const loginResult = await loginWithEmail(emailValue)
      if (!loginResult) {
        return { success: false, message: 'Failed to login. Please try again.' }
      }

      // Send credentials email with "exist" as password (non-critical)
      await sendUserCredentials(emailValue, 'exist')
    } else if ('at' in userResult && 'password' in userResult) {
      // New user created
      // Send credentials email (non-critical)
      await sendUserCredentials(emailValue, userResult.password)
    }

    return { success: true }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const emailValue = email.trim()
    if (!emailValue) {
      setError('Please enter your email address')
      return
    }
    if (!validateEmail(emailValue)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      const result = await collectEmail(emailValue)

      if (result.success) {
        localStorage.setItem('notifyer_user_email', emailValue)
        posthog?.identify(emailValue, { email: emailValue })
        posthog?.capture('free_trial_started', { email: emailValue, source: source || 'email_modal' })
        setShowSuccess(true)

        setTimeout(() => {
          const baseUrl =
            window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1' ||
              window.location.hostname.includes('localhost')
              ? 'http://localhost:3000/pricing'
              : 'https://api.insightssystem.com/api:ox_LN9zX/fb/ad/pass/notifyer-landing-youtube'

          const params = new URLSearchParams()
          params.append('email', emailValue)

          // Add all cookies to params
          const cookies = document.cookie.split(';')
          cookies.forEach((cookie) => {
            const [name, value] = cookie.trim().split('=')
            if (name && value) {
              params.append(name, decodeURIComponent(value))
            }
          })

          const finalUrl = `${baseUrl}?${params.toString()}`
          window.location.href = finalUrl
        }, 1200)
      } else {
        setError(result.message || 'Something went wrong. Please try again.')
        setIsLoading(false)
      }
    } catch (err) {
      console.error('Email modal: Email collection error:', err)
      posthog?.captureException(err)
      setError('Something went wrong. Please try again.')
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 py-6"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {!showSuccess ? (
          <>
            <h3 className="mb-2 text-xl font-semibold text-[#1c2329]">Start your free trial</h3>
            <p className="mb-5 text-sm text-[#656f68]">Enter your email to create your Notifyer workspace.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                  }}
                  placeholder="Enter your email address"
                  className="w-full rounded-lg border border-[#e7e7e7] px-4 py-3 text-[#1c2329] placeholder:text-[#656f68] focus:border-[#01cd74] focus:outline-none"
                  disabled={isLoading}
                  required
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-full bg-[#1c2329] px-6 py-3 text-sm font-semibold text-white shadow-[0px_4px_10px_rgba(0,0,0,0.08)] transition hover:-translate-y-0.5 hover:bg-[#0f1720] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? 'Creating account...' : 'Start Free Trial'}
              </button>
            </form>
          </>
        ) : (
          <div className="py-4 text-center">
            <div className="mb-3 flex justify-center">
              <div className="relative">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#01cd74] to-[#01cd74]/80 shadow-lg">
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="absolute inset-0 rounded-full bg-[#01cd74]/20 animate-ping" />
              </div>
            </div>
            <p className="text-sm text-[#1c2329]">
              Confirmation sent to <span className="font-semibold text-[#01cd74]">{email}</span>
            </p>
            <p className="mt-1 text-xs text-[#656f68]">Redirecting to your account...</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmailCaptureModal


